import { CLOUDINARY_CONFIG } from '@/constants/constants';
import eventTypeService from '@/services/eventType.service';
import slugService from '@/services/slug.service';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { ChangeEvent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';
import { toast } from 'react-toastify';
import { boolean, object, string } from 'yup';
import { ISectionGeneralProps } from './SectionGeneral';
import {
	checkIfThereAreConnectedCalendars,
	computeLocation,
	computeQuestions,
	computeTags,
	parseLocation,
	parseLocationMeta,
} from './sectionGenera.service';

const LOCATION_OPTIONS = ['GOOGLE_MEET', 'MS_TEAMS', 'FIXED', 'BOOKER_PHONE', 'BOOKER_SELECTION'] as const;

/**
 * Used on the select only. Backend has its own enum
 */
export type ILocationOption = (typeof LOCATION_OPTIONS)[number];

export default function useSectionGeneral(props: ISectionGeneralProps) {
	const { t } = useTranslation();
	const isFetching = useIsFetching() > 0;
	const isMutating = useIsMutating() > 0;

	const schema = object().shape({
		shouldAutoComputeStep: boolean().required(t('Required')),
		name: string().required(t('Required')),
		description: string(),
		imageUrl: string().ensure().default(''),
		duration: string().required(t('Required')),
		slug: string().required(t('Required')).min(2).max(36).matches(/[a-z]/),
		step: string().required(t('Required')),
		locationMeta: string().ensure(),
		location: string()
			.oneOf(LOCATION_OPTIONS)
			.required(t('Required'))
			.test('location', function validateLocation(this, value) {
				const { path, createError } = this;

				if (value === 'GOOGLE_MEET' && !checkIfThereAreConnectedCalendars(props.service, 'GOOGLE')) {
					return createError({ path, message: t('Connect at least one Google calendar to enable this option.') });
				}

				if (value === 'MS_TEAMS' && !checkIfThereAreConnectedCalendars(props.service, 'MICROSOFT')) {
					return createError({ path, message: t('Connect at least one Microsoft calendar to enable this option.') });
				}

				return true;
			}),
	});

	const {
		register,
		handleSubmit,
		setValue,
		getValues,
		watch,
		trigger,
		formState: { errors },
	} = useForm({
		defaultValues: {
			location: parseLocation(props.service.location),
			slug: props.service.slug,
			step: props.service.step,
			locationMeta: parseLocationMeta(props.service.location) || '',
			duration: props.service.duration,
			description: props.service.description,
			imageUrl: props.service.imageUrl || '',
			name: props.service.name,
			shouldAutoComputeStep: props.service.tags.ttStepMode === 'auto',
		},
		resolver: yupResolver(schema),
		mode: 'all',
	});

	useEffect(
		function validateLocation() {
			const location = getValues('location');
			if (location === 'GOOGLE_MEET' || location === 'MS_TEAMS') {
				trigger('location', { shouldFocus: true });
			}
		},
		[getValues, trigger, props.service]
	);

	const onSubmit = handleSubmit((e) => {
		const location = computeLocation(e);
		const step = e.shouldAutoComputeStep ? eventTypeService.computeStepFromDuration(e.duration) : e.step;
		const questions = computeQuestions(e.location, props.service);
		const tags = computeTags(props.service.tags, !!e.shouldAutoComputeStep);
		props.onChange({ ...e, step, location, questions, tags });
	});

	function handleChangeImageUrlClick() {
		const widget = (window as any).cloudinary.createUploadWidget(CLOUDINARY_CONFIG, (error: any, result: any) => {
			if (!(window as any).cloudinary) {
				toast.error(t('This function is temporarily unavailable.') as string);
				return;
			}

			if (error) {
				toast.error(t('Cannot upload image') as string);
			}

			if (!error && result?.event === 'success') {
				setValue('imageUrl', result.info.secure_url);
				props.onChange({ imageUrl: result.info.secure_url });
				toast.success(t('Image uploaded') as string);
			}
		});
		widget.open();
	}

	function handleDeleteImageClick() {
		setValue('imageUrl', '');
		props.onChange({ imageUrl: '' });
	}

	/**
	 * Custom function that submits the form on change
	 */
	function registerSubmitOnChange(id: any, options?: any) {
		const originalValues = register(id, options);

		function onChange(e: ChangeEvent) {
			originalValues.onChange(e);
			onSubmit();
		}

		return { ...originalValues, onChange };
	}

	function registerSlug(id: any, options?: any) {
		const originalValues = register(id, options);

		function onChange(e: ChangeEvent<HTMLInputElement>) {
			e.target.value = slugService.sanitize(e.target.value);
			originalValues.onChange(e);
		}

		function onBlur(e: ChangeEvent<HTMLInputElement>) {
			e.target.value = slugService.sanitize(e.target.value);
			originalValues.onBlur(e);
			onSubmit();
		}

		return { ...originalValues, onBlur, onChange };
	}

	/**
	 * Custom function that submits the form on change
	 */
	function registerSubmitOnBlur(id: any, options?: any) {
		const originalValues = register(id, options);

		function onBlur(e: ChangeEvent) {
			originalValues.onBlur(e);
			onSubmit();
		}

		return { ...originalValues, onBlur };
	}

	/**
	 * The location field is build using react-select
	 * The emitted event is not a change event, so we need to register it manually.
	 * We also need to submit the form on change
	 */
	function registerLocationField() {
		const originalValues = register('location');

		function onChange(
			e: SingleValue<
				| { label: string; value: string; isDisabled: boolean }
				| MultiValue<{ value: string; label: string; isDisabled: boolean }>
			>
		) {
			// @ts-expect-error
			setValue('location', e.value);
			onSubmit();
		}

		return { ...originalValues, onChange };
	}

	function _computeLocationOptions() {
		return [
			{
				label: t('Google Meet'),
				value: 'GOOGLE_MEET' as ILocationOption,
				isDisabled: !checkIfThereAreConnectedCalendars(props.service, 'GOOGLE'),
			},
			{
				label: t('Microsoft Teams'),
				value: 'MS_TEAMS' as ILocationOption,
				isDisabled: !checkIfThereAreConnectedCalendars(props.service, 'MICROSOFT'),
			},
			{ label: t('Custom location'), value: 'FIXED' as ILocationOption, isDisabled: false },
			{ label: t('Phone'), value: 'BOOKER_PHONE' as ILocationOption, isDisabled: false },
			{ label: t('Let the booker choose'), value: 'BOOKER_SELECTION' as ILocationOption, isDisabled: false },
		];
	}

	return {
		handleChangeImageUrlClick,
		handleDeleteImageClick,
		onSubmit,
		register,
		registerLocationField,
		registerSlug,
		registerSubmitOnBlur,
		registerSubmitOnChange,
		t,
		errors,
		link: eventTypeService.getLink(props.service.fullSlug),
		isLoading: isFetching || isMutating,
		isPhysicalLocationFieldVisible: getValues('location') === 'FIXED',
		shouldAutoComputeStep: watch('shouldAutoComputeStep'),
		values: getValues(),
		locationOptions: _computeLocationOptions(),
	};
}
